import React, { useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import {
  Activity, Target, Database, GitMerge, Shield, ChevronDown,
  ChevronRight, CheckCircle2, AlertTriangle, ArrowRight,
  BarChart3, Zap, FileText, TrendingUp, Eye, Layers,
  RefreshCw, Clock, Filter, Factory, RotateCw, Crosshair
} from 'lucide-react';
import { IndustryMatricesTab, ReinforcementLoopsTab, CascadeSimulatorTab } from './GraphDashboardTabs';
import { apiFetch } from '../../../services/api_service';


const API = process.env.REACT_APP_BACKEND_URL;

// Auth-aware fetch for graph endpoints — reads body as text first to avoid clone errors
async function gFetch(url, opts = {}) {
  const token = localStorage.getItem("token") || "";
  const resp = await fetch(url, {
    ...opts,
    cache: "no-store",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(opts.headers || {}) },
  });
  const text = await resp.text();
  return { ok: resp.ok, status: resp.status, json: () => JSON.parse(text) };
}

// ============================================================================
// Color tokens
// ============================================================================
const STATUS_COLORS = {
  approved: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', dot: 'bg-emerald-500' },
  in_review: { bg: 'bg-amber-500/15', text: 'text-amber-400', dot: 'bg-amber-500' },
  collecting: { bg: 'bg-blue-500/15', text: 'text-blue-400', dot: 'bg-blue-500' },
  not_started: { bg: 'bg-zinc-500/15', text: 'text-zinc-400', dot: 'bg-zinc-500' },
};
const VERDICT_COLORS = {
  material: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', ring: 'ring-emerald-500/40' },
  not_material: { bg: 'bg-zinc-700/40', text: 'text-zinc-400', ring: 'ring-zinc-600/40' },
};
const PILLAR_COLORS = { E: 'text-emerald-400', S: 'text-blue-400', G: 'text-amber-400' };

// ============================================================================
// Small components
// ============================================================================
function Badge({ children, color = 'zinc' }) {
  const map = {
    emerald: 'bg-emerald-500/15 text-emerald-400',
    blue: 'bg-blue-500/15 text-blue-400',
    amber: 'bg-amber-500/15 text-amber-400',
    red: 'bg-red-500/15 text-red-400',
    zinc: 'bg-zinc-700/50 text-zinc-400',
    purple: 'bg-purple-500/15 text-purple-400',
    cyan: 'bg-cyan-500/15 text-cyan-400',
    teal: 'bg-teal-500/15 text-teal-400',
  };
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${map[color]}`}>{children}</span>;
}

function StatCard({ icon: Icon, label, value, sub, accent = 'emerald' }) {
  const acc = { emerald: 'text-emerald-400', amber: 'text-amber-400', blue: 'text-blue-400', red: 'text-red-400', purple: 'text-purple-400' };
  return (
    <div data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`} className="bg-[#141414] border border-[#1F1F1F] rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className={acc[accent]} />
        <span className="text-xs text-zinc-500 uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-2xl font-semibold ${acc[accent]}`}>{value}</p>
      {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
    </div>
  );
}

function formatTimestamp(ts) {
  if (!ts) return '';
  if (typeof ts === 'string') return ts.split('.')[0]?.replace('T', ' ');
  if (typeof ts === 'object') {
    try { return new Date(ts).toISOString().split('.')[0]?.replace('T', ' '); } catch { return String(ts); }
  }
  return String(ts);
}

function ProgressBar({ pct, color = 'emerald' }) {
  const cls = { emerald: 'bg-emerald-500', amber: 'bg-amber-500', blue: 'bg-blue-500', red: 'bg-red-500' };
  return (
    <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden w-full">
      <div className={`h-full ${cls[color]} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
    </div>
  );
}

// ============================================================================
// View: DASHBOARD
// ============================================================================
function DashboardView({ dashboard, readiness, esrsStatus }) {
  if (!dashboard) return null;
  const materiality = dashboard.materiality || { total: 0, material: 0 };
  const datapoints = dashboard.datapoints || { total: 0, approved: 0 };
  const evidence = dashboard.evidence || { total: 0, verified: 0 };
  const esrs = dashboard.esrs || {};
  const materialPct = materiality.total ? Math.round((materiality.material / materiality.total) * 100) : 0;
  const approvedPct = datapoints.total ? Math.round((datapoints.approved / datapoints.total) * 100) : 0;
  const verifiedPct = evidence.total ? Math.round((evidence.verified / evidence.total) * 100) : 0;

  return (
    <div data-testid="dashboard-view" className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard icon={Target} label="Material IROs" value={materiality.material} sub={`${materialPct}% of ${materiality.total} topics`} accent="emerald" />
        <StatCard icon={Database} label="Active Datapoints" value={datapoints.total} sub={`${datapoints.approved} approved`} accent="blue" />
        <StatCard icon={CheckCircle2} label="Data Approved" value={`${approvedPct}%`} sub={`${datapoints.approved}/${datapoints.total} datapoints`} accent="emerald" />
        <StatCard icon={FileText} label="Evidence Docs" value={evidence.total} sub={`${verifiedPct}% verified`} accent="purple" />
        <StatCard icon={Zap} label="ESRS Active" value={esrs.active || 0} sub={`${esrs.inactive || 0} inactive`} accent="amber" />
      </div>

      <div className="bg-[#141414] border border-[#1F1F1F] rounded-lg p-5">
        <h3 className="text-sm font-medium text-zinc-300 mb-4 flex items-center gap-2">
          <BarChart3 size={14} className="text-emerald-400" />Framework Export Readiness
        </h3>
        <div className="space-y-3">
          {readiness && readiness.map(fw => (
            <div key={fw.fw_id} data-testid={`fw-readiness-${fw.fw_id}`} className="flex items-center gap-4">
              <span className="text-xs text-zinc-400 w-32 shrink-0">{fw.fw_name}</span>
              <div className="flex-1"><ProgressBar pct={fw.readiness_pct} color={fw.export_ready ? 'emerald' : fw.readiness_pct > 50 ? 'amber' : 'red'} /></div>
              <span className={`text-xs font-mono w-12 text-right ${fw.export_ready ? 'text-emerald-400' : 'text-zinc-400'}`}>{fw.readiness_pct}%</span>
              <span className="text-xs text-zinc-600 w-16">{fw.satisfied}/{fw.total_requirements}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ESRS Disclosure Progress */}
      {esrsStatus?.length > 0 && (
        <div className="bg-[#141414] border border-[#1F1F1F] rounded-lg p-5">
          <h3 className="text-sm font-medium text-zinc-300 mb-4 flex items-center gap-2">
            <Zap size={14} className="text-amber-400" />ESRS Disclosure Progress
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {esrsStatus.map(t => {
              const pct = t.pct || 0;
              return (
                <div key={t.topic} className="bg-[#0D0D0D] rounded p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-zinc-400 font-medium">{t.topic}</span>
                    <span className={`text-xs font-mono ${pct >= 50 ? 'text-emerald-400' : 'text-red-400'}`}>{pct}%</span>
                  </div>
                  <ProgressBar pct={pct} color={pct === 100 ? 'emerald' : pct >= 50 ? 'amber' : 'red'} />
                  <p className="text-xs text-zinc-600 mt-1">{t.satisfied}/{t.total_req} reqs.</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {dashboard.recent_events?.length > 0 && (
        <div className="bg-[#141414] border border-[#1F1F1F] rounded-lg p-5">
          <h3 className="text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2">
            <Clock size={14} className="text-zinc-500" />Recent Events
          </h3>
          <div className="space-y-2">
            {dashboard.recent_events.map(ev => (
              <div key={ev.id} className="flex items-center gap-3 text-xs">
                <span className="text-zinc-600 font-mono w-36 shrink-0">{formatTimestamp(ev.timestamp)}</span>
                <Badge color="blue">{ev.action?.split('.').pop()}</Badge>
                <span className="text-zinc-400 truncate">{ev.detail}</span>
                <span className="text-zinc-600 ml-auto shrink-0">by {ev.actor}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// View: MATERIALITY (Master Switch)
// ============================================================================
function MaterialityView({ iros, onScoreIRO, loadingIroId }) {
  const [expandedIro, setExpandedIro] = useState(null);
  const [cascade, setCascade] = useState(null);
  const [cascadeLoading, setCascadeLoading] = useState(false);

  const loadCascade = async (iroId) => {
    if (expandedIro === iroId) { setExpandedIro(null); setCascade(null); return; }
    setExpandedIro(iroId);
    setCascadeLoading(true);
    try {
      const res = await gFetch(`${API}/api/v1/graph/materiality/cascade/${iroId}?depth=3`);
      setCascade(await res.json());
    } catch (e) { console.error(e); }
    setCascadeLoading(false);
  };

  return (
    <div data-testid="materiality-view" className="space-y-3">
      {iros.map(row => {
        const iro = row.iro;
        const vc = VERDICT_COLORS[iro.verdict];
        const isExpanded = expandedIro === iro.id;
        return (
          <div key={iro.id} data-testid={`iro-${iro.esrs_topic}`} className={`bg-[#141414] border ${isExpanded ? 'border-emerald-500/30' : 'border-[#1F1F1F]'} rounded-lg overflow-hidden`}>
            <div className="flex items-center gap-3 p-4 cursor-pointer hover:bg-[#1A1A1A] transition-colors" onClick={() => loadCascade(iro.id)}>
              {isExpanded ? <ChevronDown size={14} className="text-zinc-500" /> : <ChevronRight size={14} className="text-zinc-500" />}
              <span className={`text-xs font-bold ${PILLAR_COLORS[iro.pillar]}`}>{iro.esrs_topic}</span>
              <span className="text-sm text-zinc-200 font-medium flex-1">{iro.name}</span>
              <div className="flex items-center gap-2">
                <Badge color={iro.verdict === 'material' ? 'emerald' : 'zinc'}>{iro.verdict?.toUpperCase()}</Badge>
                <span className="text-xs text-zinc-500">I:{iro.impact_score} F:{iro.financial_score}</span>
                <span className="text-xs text-zinc-600">{row.datapoint_count} DPs | {row.esrs_count} ESRS</span>
              </div>
              <button
                data-testid={`toggle-${iro.esrs_topic}`}
                disabled={loadingIroId === iro.id}
                onClick={(e) => {
                  e.stopPropagation();
                  onScoreIRO(iro.id, {
                    verdict: iro.verdict === 'material' ? 'not_material' : 'material',
                    impact_severity: iro.impact_severity,
                    impact_likelihood: iro.impact_likelihood,
                    financial_magnitude: iro.financial_magnitude,
                    financial_likelihood: iro.financial_likelihood,
                    rationale: iro.verdict === 'material' ? 'Dematerialized via master switch' : 'Materialized via master switch',
                  });
                }}
                className={`ml-2 px-3 py-1 rounded text-xs font-medium transition-all ${
                  iro.verdict === 'material'
                    ? 'bg-emerald-500/20 text-emerald-400 hover:bg-red-500/20 hover:text-red-400'
                    : 'bg-zinc-700 text-zinc-400 hover:bg-emerald-500/20 hover:text-emerald-400'
                } ${loadingIroId === iro.id ? 'opacity-50 cursor-wait' : ''}`}
              >
                {loadingIroId === iro.id ? <RefreshCw size={12} className="animate-spin" /> : iro.verdict === 'material' ? 'Deactivate' : 'Activate'}
              </button>
            </div>

            {isExpanded && (
              <div className="border-t border-[#1F1F1F] p-4 bg-[#0D0D0D]">
                {cascadeLoading ? (
                  <div className="flex items-center gap-2 text-zinc-500 text-xs"><RefreshCw size={12} className="animate-spin" /> Loading cascade...</div>
                ) : cascade ? (
                  <CascadeDetail cascade={cascade} />
                ) : null}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CascadeDetail({ cascade }) {
  const { iro, activated_datapoints, esrs_activated, summary } = cascade;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-[#141414] rounded p-3">
          <p className="text-xs text-zinc-500">Active DPs</p>
          <p className="text-lg font-semibold text-emerald-400">{summary.active}</p>
        </div>
        <div className="bg-[#141414] rounded p-3">
          <p className="text-xs text-zinc-500">Approved</p>
          <p className="text-lg font-semibold text-blue-400">{summary.approved}</p>
        </div>
        <div className="bg-[#141414] rounded p-3">
          <p className="text-xs text-zinc-500">ESRS Req.</p>
          <p className="text-lg font-semibold text-amber-400">{esrs_activated?.length || 0}</p>
        </div>
        <div className="bg-[#141414] rounded p-3">
          <p className="text-xs text-zinc-500">Gaps</p>
          <p className="text-lg font-semibold text-red-400">{summary.gaps}</p>
        </div>
      </div>

      {activated_datapoints?.length > 0 && (
        <div>
          <p className="text-xs text-zinc-500 mb-2 uppercase tracking-wider">Activated Datapoints</p>
          <div className="space-y-1.5">
            {activated_datapoints.map(dp => {
              const sc = STATUS_COLORS[dp.status] || STATUS_COLORS.not_started;
              return (
                <div key={dp.id} data-testid={`dp-${dp.name?.replace(/\s/g, '-')}`} className="flex items-center gap-3 bg-[#141414] rounded px-3 py-2">
                  <div className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                  <span className="text-xs text-zinc-300 flex-1">{dp.name}</span>
                  <Badge color={dp.status === 'approved' ? 'emerald' : dp.status === 'in_review' ? 'amber' : 'blue'}>{dp.status}</Badge>
                  <span className="text-xs text-zinc-500 font-mono">{dp.value} {dp.unit}</span>
                  {dp.fw_requirements?.length > 0 && (
                    <span className="text-xs text-zinc-600">{dp.fw_requirements.length} FWs</span>
                  )}
                  {dp.gaps?.length > 0 && (
                    <span className="flex items-center gap-1 text-xs text-red-400">
                      <AlertTriangle size={10} />{dp.gaps.join(', ')}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {esrs_activated?.length > 0 && (
        <div>
          <p className="text-xs text-zinc-500 mb-2 uppercase tracking-wider">ESRS Requirements Activated</p>
          <div className="flex flex-wrap gap-1.5">
            {esrs_activated.map(er => (
              <span key={er.code} className="px-2 py-0.5 bg-amber-500/10 text-amber-400 text-xs rounded">{er.code}: {er.label?.substring(0, 50)}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// View: CROSS-FW MATRIX
// ============================================================================
function CrossMapView({ matrix }) {
  if (!matrix?.summary || !Array.isArray(matrix?.matrix)) return null;
  const FW_ORDER = ['gri', 'tcfd', 'cdp', 'sbti', 'tnfd', 'sasb', 'ghgp'];
  const FW_LABELS = { gri: 'GRI', tcfd: 'TCFD', cdp: 'CDP', sbti: 'SBTi', tnfd: 'TNFD', sasb: 'SASB', ghgp: 'GHG P.' };

  return (
    <div data-testid="crossmap-view" className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <StatCard icon={Database} label="Datapoints" value={matrix.summary.total_datapoints} accent="blue" />
        <StatCard icon={GitMerge} label="FW Mappings" value={matrix.summary.total_mappings} accent="purple" />
        <StatCard icon={TrendingUp} label="Efficiency" value={`${matrix.summary.efficiency_ratio}x`} sub="avg frameworks per datapoint" accent="emerald" />
      </div>

      <div className="bg-[#141414] border border-[#1F1F1F] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[#1F1F1F]">
                <th className="text-left p-3 text-zinc-500 font-medium w-52">Datapoint</th>
                <th className="text-center p-3 text-zinc-500 font-medium w-16">Status</th>
                {FW_ORDER.map(fw => (
                  <th key={fw} className="text-center p-2 text-zinc-500 font-medium w-16">{FW_LABELS[fw]}</th>
                ))}
                <th className="text-center p-3 text-zinc-500 font-medium w-16">Ev.</th>
              </tr>
            </thead>
            <tbody>
              {matrix.matrix.map((item, i) => {
                const dp = item.datapoint;
                const sc = STATUS_COLORS[dp.status] || STATUS_COLORS.not_started;
                return (
                  <tr key={i} className="border-b border-[#1F1F1F]/50 hover:bg-[#1A1A1A]">
                    <td className="p-3">
                      <div className="text-zinc-300">{dp.name}</div>
                      <div className="text-zinc-600 mt-0.5">{dp.esrs_code}</div>
                    </td>
                    <td className="text-center p-2"><Badge color={dp.status === 'approved' ? 'emerald' : dp.status === 'in_review' ? 'amber' : 'blue'}>{dp.status}</Badge></td>
                    {FW_ORDER.map(fw => {
                      const mapping = item.frameworks[fw];
                      return (
                        <td key={fw} className="text-center p-2">
                          {mapping ? (
                            <span className={`inline-block w-6 h-6 rounded-sm flex items-center justify-center text-xs ${
                              mapping.satisfied ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-700/50 text-zinc-500'
                            }`}>
                              {mapping.satisfied ? <CheckCircle2 size={10} /> : mapping.code?.split(' ').pop()?.substring(0, 3)}
                            </span>
                          ) : <span className="text-zinc-800">-</span>}
                        </td>
                      );
                    })}
                    <td className="text-center p-2 text-zinc-500">{item.evidence_count || 0}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {matrix.summary.per_fw && Object.keys(matrix.summary.per_fw).length > 0 && (
        <div className="bg-[#141414] border border-[#1F1F1F] rounded-lg p-5">
          <h3 className="text-sm font-medium text-zinc-300 mb-3">Per-Framework Coverage</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {Object.entries(matrix.summary.per_fw).map(([fwId, fw]) => {
              const pct = fw.total > 0 ? Math.round((fw.satisfied / fw.total) * 100) : 0;
              return (
                <div key={fwId} className="bg-[#0D0D0D] rounded p-3">
                  <p className="text-xs text-zinc-400 mb-1">{FW_LABELS[fwId] || fwId}</p>
                  <p className="text-lg font-semibold text-zinc-200">{pct}%</p>
                  <ProgressBar pct={pct} color={pct === 100 ? 'emerald' : pct > 50 ? 'amber' : 'red'} />
                  <p className="text-xs text-zinc-600 mt-1">{fw.satisfied}/{fw.total} satisfied</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// View: AUDIT TRAIL
// ============================================================================
function AuditView({ events }) {
  if (!events?.length) return <p className="text-zinc-500 text-sm">No audit events yet. Score an IRO or update a datapoint to create the first event.</p>;
  return (
    <div data-testid="audit-view" className="space-y-2">
      {events.map(ev => (
        <div key={ev.id} className="bg-[#141414] border border-[#1F1F1F] rounded-lg p-3 flex items-start gap-3">
          <div className="mt-0.5"><Shield size={12} className="text-amber-500" /></div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge color="blue">{ev.action?.split('.').pop()}</Badge>
              <span className="text-xs text-zinc-500">{ev.module}</span>
              <span className="text-xs text-zinc-600 ml-auto">{formatTimestamp(ev.timestamp)}</span>
            </div>
            <p className="text-xs text-zinc-300">{ev.detail}</p>
            <div className="flex items-center gap-3 mt-1 text-xs text-zinc-600">
              <span>Actor: {ev.actor} ({ev.actor_type})</span>
              {ev.entity_type && <span>Entity: {ev.entity_type}/{ev.entity_id?.substring(0, 8)}</span>}
              <span className="font-mono text-zinc-700 ml-auto" title={ev.event_hash}>Hash: {ev.event_hash?.substring(0, 12)}...</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// View: ESRS DISCLOSURE STATUS
// ============================================================================
function ESRSView({ esrsStatus }) {
  if (!esrsStatus?.length) return <p className="text-zinc-500 text-sm">No ESRS data available.</p>;
  const topicNames = { E1: 'Climate Change', E2: 'Pollution', E3: 'Water & Marine', E4: 'Biodiversity', E5: 'Circular Economy', S1: 'Own Workforce', S2: 'Value Chain Workers', S3: 'Affected Communities', S4: 'Consumers', G1: 'Business Conduct' };
  const topicColors = { E: 'emerald', S: 'blue', G: 'amber' };

  return (
    <div data-testid="esrs-view" className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <StatCard icon={Zap} label="Active Topics" value={esrsStatus.length} accent="amber" />
        <StatCard icon={CheckCircle2} label="Avg. Completion" value={`${Math.round(esrsStatus.reduce((s, t) => s + (t.pct || 0), 0) / esrsStatus.length)}%`} accent="emerald" />
        <StatCard icon={AlertTriangle} label="Topics < 50%" value={esrsStatus.filter(t => (t.pct || 0) < 50).length} accent="red" />
      </div>
      <div className="space-y-2">
        {esrsStatus.map(t => {
          const pillar = t.topic?.[0] || 'E';
          const color = topicColors[pillar] || 'emerald';
          return (
            <div key={t.topic} data-testid={`esrs-${t.topic}`} className="bg-[#141414] border border-[#1F1F1F] rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Badge color={color}>{t.topic}</Badge>
                  <span className="text-sm text-zinc-300">{topicNames[t.topic] || t.topic}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-zinc-500">{t.satisfied}/{t.total_req} requirements</span>
                  <span className={`text-sm font-semibold ${(t.pct || 0) >= 50 ? 'text-emerald-400' : 'text-red-400'}`}>{t.pct || 0}%</span>
                </div>
              </div>
              <ProgressBar pct={t.pct || 0} color={(t.pct || 0) === 100 ? 'emerald' : (t.pct || 0) >= 50 ? 'amber' : 'red'} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN: GraphDashboard
// ============================================================================
const VIEWS = [
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { id: 'materiality', label: 'Materiality Switch', icon: Target },
  { id: 'crossmap', label: 'Cross-FW Matrix', icon: GitMerge },
  { id: 'esrs', label: 'ESRS Status', icon: Zap },
  { id: 'audit', label: 'Audit Trail', icon: Shield },
  { id: 'industry', label: 'Industry Matrices', icon: Factory },
  { id: 'loops', label: 'Reinforcement Loops', icon: RotateCw },
  { id: 'simulator', label: 'Cascade Simulator', icon: Crosshair },
];

export default function GraphDashboard() {
  const [view, setView] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [iros, setIros] = useState([]);
  const [crossmap, setCrossmap] = useState(null);
  const [readiness, setReadiness] = useState([]);
  const [auditEvents, setAuditEvents] = useState([]);
  const [esrsStatus, setEsrsStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingIroId, setLoadingIroId] = useState(null);
  const [realtimeEvent, setRealtimeEvent] = useState(null);
  const socketRef = useRef(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, iroRes, xmapRes, readRes, auditRes, esrsRes] = await Promise.all([
        gFetch(`${API}/api/v1/graph/dashboard/summary`),
        gFetch(`${API}/api/v1/graph/materiality/iros`),
        gFetch(`${API}/api/v1/graph/crossmap/matrix`),
        gFetch(`${API}/api/v1/graph/export/readiness`),
        gFetch(`${API}/api/v1/graph/audit/trail?limit=50`),
        gFetch(`${API}/api/v1/graph/esrs/disclosure-status`),
      ]);
      setDashboard(await dashRes.json());
      const iroData = await iroRes.json();
      setIros(iroData.iros || []);
      setCrossmap(await xmapRes.json());
      const readData = await readRes.json();
      setReadiness(readData.frameworks || []);
      const auditData = await auditRes.json();
      setAuditEvents(auditData.events || []);
      const esrsData = await esrsRes.json();
      setEsrsStatus(esrsData.topics || []);
    } catch (e) { console.error('Fetch error:', e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // WebSocket: listen for real-time graph events
  useEffect(() => {
    try {
      const socket = io(API, { path: '/api/socket.io', transports: ['websocket', 'polling'] });
      socketRef.current = socket;

      socket.on('graph_event', (data) => {
        setRealtimeEvent({ ...data, receivedAt: Date.now() });
        // Auto-refresh data when cascade events arrive
        fetchAll();
      });

      return () => { socket.disconnect(); };
    } catch (e) {
      console.warn('WebSocket not available, using manual refresh');
    }
  }, [fetchAll]);

  const handleScoreIRO = async (iroId, data) => {
    setLoadingIroId(iroId);
    try {
      const res = await gFetch(`${API}/api/v1/graph/materiality/iro/${iroId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (res.ok) await fetchAll();
    } catch (e) { console.error(e); }
    setLoadingIroId(null);
  };

  return (
    <div data-testid="graph-dashboard" className="min-h-screen bg-[#0A0A0A] text-zinc-100">
      {/* Header */}
      <div className="border-b border-[#1F1F1F] bg-[#0D0D0D] px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-zinc-100 flex items-center gap-2">
              <Layers size={20} className="text-emerald-400" />
              Graph Intelligence
            </h1>
            <p className="text-xs text-zinc-500 mt-0.5">Neo4j-powered materiality cascade with real-time propagation</p>
          </div>
          <button data-testid="refresh-btn" onClick={fetchAll} disabled={loading}
            className="flex items-center gap-2 px-3 py-1.5 bg-[#1A1A1A] border border-[#2A2A2A] rounded text-xs text-zinc-400 hover:text-zinc-200 transition-colors">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />Refresh
          </button>
        </div>

        {/* View Tabs */}
        <div className="flex items-center gap-1 mt-4">
          {VIEWS.map(v => (
            <button key={v.id} data-testid={`tab-${v.id}`} onClick={() => setView(v.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-all ${
                view === v.id ? 'bg-emerald-500/15 text-emerald-400' : 'text-zinc-500 hover:text-zinc-300 hover:bg-[#1A1A1A]'
              }`}>
              <v.icon size={12} />{v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Real-time event toast */}
      {realtimeEvent && Date.now() - realtimeEvent.receivedAt < 5000 && (
        <div className="mx-6 mt-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-4 py-2 flex items-center gap-2 animate-pulse">
          <Activity size={14} className="text-emerald-400" />
          <span className="text-xs text-emerald-400">Real-time update:</span>
          <span className="text-xs text-zinc-300">{realtimeEvent.topic} — {realtimeEvent.payload?.detail || 'cascade propagated'}</span>
        </div>
      )}

      {/* Content */}
      <div className="p-6 max-w-[1400px] mx-auto">
        {loading && !dashboard ? (
          <div className="flex items-center justify-center py-20 text-zinc-500">
            <RefreshCw size={16} className="animate-spin mr-2" />Loading graph data...
          </div>
        ) : (
          <>
            {view === 'dashboard' && <DashboardView dashboard={dashboard} readiness={readiness} esrsStatus={esrsStatus} />}
            {view === 'materiality' && <MaterialityView iros={iros} onScoreIRO={handleScoreIRO} loadingIroId={loadingIroId} />}
            {view === 'crossmap' && <CrossMapView matrix={crossmap} />}
            {view === 'esrs' && <ESRSView esrsStatus={esrsStatus} />}
            {view === 'audit' && <AuditView events={auditEvents} />}
            {view === 'industry' && <IndustryMatricesTab />}
            {view === 'loops' && <ReinforcementLoopsTab />}
            {view === 'simulator' && <CascadeSimulatorTab />}
          </>
        )}
      </div>
    </div>
  );
}
